import React, { useEffect, useState } from 'react';
import {
  FiDollarSign, FiShoppingBag, FiUsers, FiBox,
  FiTrendingUp, FiArrowUpRight, FiMoreVertical, FiRefreshCw
} from 'react-icons/fi';
import KPICard from '../components/KPICard';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';
import { franchiseService, orderService } from '../services/api';
import { useRealtimeApi } from '../hooks/useApi';

ChartJS.register(
  CategoryScale, LinearScale, PointElement, LineElement,
  BarElement, Title, Tooltip, Legend, Filler
);

const Dashboard = () => {
  const [dashboardStats, setDashboardStats] = useState(null);
  const [recentOrders, setRecentOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Realtime stats refresh every 60 seconds
  const {
    data: realtimeStats,
    loading: statsLoading,
    refresh: refreshStats
  } = useRealtimeApi(franchiseService.getDashboardStats, 60000);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  useEffect(() => {
    if (realtimeStats) {
      setDashboardStats(realtimeStats);
    }
  }, [realtimeStats]);

  const fetchDashboardData = async () => {
    setLoading(true);
    setError(null);

    try {
      const statsResult = await franchiseService.getDashboardStats();
      if (statsResult.success) {
        setDashboardStats(statsResult.data.data);
      } else {
        setError(statsResult.error);
      }

      const ordersResult = await orderService.getOrders({ limit: 5, sort: '-createdAt' });
      if (ordersResult.success) {
        setRecentOrders(ordersResult.data || []);
      }
    } catch (err) {
      setError('Failed to load dashboard data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Chart Data from API Response
  const lineData = {
    labels: dashboardStats?.revenueChart?.labels || [],
    datasets: [
      {
        fill: true,
        label: 'Revenue',
        data: dashboardStats?.revenueChart?.data || [],
        borderColor: '#7c3aed',
        backgroundColor: 'rgba(124, 58, 237, 0.15)',
        tension: 0.4,
      },
    ],
  };

  const barData = {
    labels: dashboardStats?.branchPerformance?.labels || [],
    datasets: [
      {
        label: 'Orders',
        data: dashboardStats?.branchPerformance?.data || [],
        backgroundColor: '#06b6d4',
        borderRadius: 8,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
    },
    scales: {
      y: {
        grid: { color: 'rgba(255,255,255,0.05)' },
        ticks: { color: '#64748b' }
      },
      x: {
        grid: { display: false },
        ticks: { color: '#64748b' }
      }
    }
  };

  if (loading && !dashboardStats) {
    return (
      <div className="container-fluid p-0 d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
        <div className="text-center">
          <div className="spinner-border text-primary mb-3" role="status" />
          <p className="text-muted">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid p-0">
      {/* Header */}
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-4">
        <div>
          <h2 className="fw-bold mb-1">Executive Overview</h2>
          <p className="text-muted small mb-0">Welcome back, here's what's happening today.</p>
        </div>
        <div className="d-flex gap-2">
          <button
            onClick={refreshStats}
            className="btn glass-card border-0 text-white px-3"
            disabled={statsLoading}
          >
            <FiRefreshCw className={statsLoading ? 'spin' : ''} />
          </button>
          <button className="btn glass-card border-0 text-white px-3">Report</button>
          <button className="btn btn-primary px-3">Manage</button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="row g-4 mb-4">
        <div className="col-xl-3 col-md-6">
          <KPICard
            title="Total Revenue"
            value={`₹${(dashboardStats?.totalRevenue || 0).toLocaleString()}`}
            icon={<FiDollarSign size={20} />}
            trend={dashboardStats?.revenueTrend?.direction || "up"}
            trendValue={dashboardStats?.revenueTrend?.value || "0"}
            color="primary"
          />
        </div>
        <div className="col-xl-3 col-md-6">
          <KPICard
            title="Total Orders"
            value={(dashboardStats?.totalOrders || 0).toLocaleString()}
            icon={<FiShoppingBag size={20} />}
            trend={dashboardStats?.ordersTrend?.direction || "up"}
            trendValue={dashboardStats?.ordersTrend?.value || "0"}
            color="secondary"
          />
        </div>
        <div className="col-xl-3 col-md-6">
          <KPICard
            title="Active Customers"
            value={(dashboardStats?.activeCustomers || 0).toLocaleString()}
            icon={<FiUsers size={20} />}
            trend={dashboardStats?.customersTrend?.direction || "up"}
            trendValue={dashboardStats?.customersTrend?.value || "0"}
            color="success"
          />
        </div>
        <div className="col-xl-3 col-md-6">
          <KPICard
            title="Inventory Value"
            value={`₹${(dashboardStats?.inventoryValue || 0).toLocaleString()}`}
            icon={<FiBox size={20} />}
            trend={dashboardStats?.inventoryTrend?.direction || "down"}
            trendValue={dashboardStats?.inventoryTrend?.value || "0"}
            color="danger"
          />
        </div>
      </div>

      {/* Charts Row */}
      <div className="row g-4">
        <div className="col-xl-8">
          <div className="glass-card p-4 h-100">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h5 className="fw-bold mb-0">Revenue Analytics</h5>
              <FiMoreVertical className="text-muted" />
            </div>
            <div style={{ height: '320px' }}>
              <Line data={lineData} options={chartOptions} />
            </div>
          </div>
        </div>

        <div className="col-xl-4">
          <div className="glass-card p-4 h-100">
            <h5 className="fw-bold mb-4">Branch Performance</h5>
            <div style={{ height: '320px' }}>
              <Bar data={barData} options={chartOptions} />
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Section - Recent Orders + Insights */}
      <div className="row g-4 mt-4">
        <div className="col-xl-6">
          <div className="glass-card p-4">
            <h5 className="fw-bold mb-4">Recent Transactions</h5>
            {/* Your table code remains the same */}
            <div className="table-responsive">
              <table className="table table-dark table-hover mb-0 border-0">
                <thead>
                  <tr className="text-muted small uppercase">
                    <th className="border-0">Customer</th>
                    <th className="border-0">Amount</th>
                    <th className="border-0">Status</th>
                    <th className="border-0">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {recentOrders.length > 0 ? (
                    recentOrders.map((order) => (
                      <tr key={order.id} className="align-middle">
                        <td className="border-0 py-3">
                          <div className="d-flex align-items-center gap-2">
                            <img
                              src={`https://i.pravatar.cc/150?u=${order.customerId}`}
                              className="rounded-circle"
                              width="30"
                              alt=""
                            />
                            <span className="small">{order.customerName || 'N/A'}</span>
                          </div>
                        </td>
                        <td className="border-0 small fw-bold">₹{order.totalAmount?.toLocaleString() || 0}</td>
                        <td className="border-0">
                          <span className={`badge bg-${order.orderStatus === 'completed' ? 'success' :
                            order.orderStatus === 'processing' ? 'warning' :
                              order.orderStatus === 'cancelled' ? 'danger' : 'secondary'
                            } bg-opacity-10 text-${order.orderStatus === 'completed' ? 'success' :
                              order.orderStatus === 'processing' ? 'warning' :
                                order.orderStatus === 'cancelled' ? 'danger' : 'secondary'
                            } small`}>
                            {order.orderStatus || 'New'}
                          </span>
                        </td>
                        <td className="border-0 text-muted small">
                          {order.createdAt ? new Date(order.createdAt).toLocaleDateString() : 'N/A'}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4" className="text-center py-4 text-muted">No recent orders</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="col-xl-6">
          <div className="glass-card p-4 h-100">
            <h5 className="fw-bold mb-4">AI Business Insights</h5>
            {/* You can make this dynamic later */}
            <div className="d-flex flex-column gap-3">
              <div className="p-3 rounded-3 bg-primary bg-opacity-10 border border-primary border-opacity-20">
                <div className="d-flex align-items-center gap-2 mb-2 text-primary fw-bold">
                  <FiTrendingUp /> Sales Prediction
                </div>
                <p className="small text-muted mb-0">Revenue is expected to grow by 15% next month based on current branch performance.</p>
              </div>
              <div className="p-3 rounded-3 bg-secondary bg-opacity-10 border border-secondary border-opacity-20">
                <div className="d-flex align-items-center gap-2 mb-2 text-secondary fw-bold">
                  <FiArrowUpRight /> Stock Alert
                </div>
                <p className="small text-muted mb-0">3 branches are low on "Premium Coffee Beans". Restocking recommended within 48 hours.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;